import React, { useEffect, useState ,useMemo, useRef} from "react";
import { IoMdClose } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useCart } from "../../context/CartContext";
import {
  FaTrash,
  FaShoppingCart,
  FaCalendarAlt,
  FaHourglassHalf,
  FaHome,
  FaRedoAlt,
} from "react-icons/fa";
import { TbRepeat } from "react-icons/tb";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";


const CartDrawer = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { cartItems, vendorId, removeFromCart, clearCart } = useCart();

  const [deleteItemId, setDeleteItemId] = useState(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const [brandName, setBrandName] = useState("");
  const [pets, setPets] = useState([]);

  const customerData = useSelector((state) => state.auth?.customer_data);
  const isLoggedIn = !!customerData;

  const SURCHARGE_LABELS = {
  size_surcharge: "Pet Size Surcharge",
  room_type_surcharge: "Room Type Surcharge",
  coat_surcharge: "Coat Surcharge",     
  breed_surcharge: "Breed Surcharge",
};

const getSurchargeLabel = (name) =>
  SURCHARGE_LABELS[name] || name.replace(/_/g, " ");


const validatedCart = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to compare only dates

    return cartItems.map((item) => {
      const itemDate = new Date(item.start_date || item.date);
      itemDate.setHours(0, 0, 0, 0);
      
      return {
        ...item,
        isExpired: itemDate < today,
      };
    });
  }, [cartItems]);

  const hasCleanedExpired = useRef(false);
  
  useEffect(() => {
    if (!cartItems.length || hasCleanedExpired.current) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const expiredIds = cartItems
      .filter((item) => {
        const itemDate = new Date(item.start_date || item.date);
        itemDate.setHours(0, 0, 0, 0);
        return itemDate < today;
      })
      .map((item) => item.cartItemId || item._id);

    if (expiredIds.length > 0) {
      hasCleanedExpired.current = true; // ✅ move before updates

      expiredIds.forEach((id) =>
        removeFromCart(id, { silent: true }) // ✅ silent
      );

      toast.warning("Expired items were removed from your cart");
    }
  }, [cartItems, removeFromCart]);

  const hasExpiredItems = validatedCart.some((item) => item.isExpired);

  

  /* ------------------ BRAND NAME ------------------ */
  useEffect(() => {
    const brand = localStorage.getItem("vendor_brand") || "";
    setBrandName(brand);
  }, []);

  /* ------------------ SCROLL LOCK ------------------ */
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  /* ------------------ HELPERS ------------------ */

  const formatDDMMYYYY = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  };

  const getDateDisplay = (item) => {
    if (item.start_date) {
      const start = formatDDMMYYYY(item.start_date);

      if (item.item_type === "boarding" && item.end_date) {
        const end = formatDDMMYYYY(item.end_date);
        return `${start} → ${end}`;
      }

      if (item.time_range) {
        return `${start} | ${item.time_range}`;
      }

      return start;
    }

    return item.date || item.time_range || "Date not set";
  };

  const getItemType = (serviceType) => {
    if (!serviceType) return "service";
    const type = serviceType.toLowerCase();

    if (type.includes("boarding")) return "boarding";
    if (type.includes("grooming")) return "grooming";
    if (type.includes("day care") || type.includes("walk")) return "daycare";
    if (type.includes("vet") || type.includes("clinic")) {
        return "pet_vet clinic";
    }

    return "service";
};
 
  const getPetName = (item) => {
    if (!isLoggedIn) return item.selectedPetName || "Pet not selected";
    const pet = pets.find((p) => p._id === item.vendor_pet_id);
    return pet?.name || item.selectedPetName || "Pet not selected";
  };

  /* ------------------ TOTAL ------------------ */

  const subtotal = cartItems.reduce((sum, item) => {
    const price = item.unit_price || item.price || 0;
    const qty = item.quantity || 1;
    return sum + price * qty;
  }, 0);

  const itemToDelete = cartItems.find(
  (item) => (item.cartItemId || item._id) === deleteItemId
);

  const displayBrand = cartItems.length > 0 ? cartItems[0].vendor_name : "";
  /* ------------------ UI ------------------ */

  return createPortal(
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[999]"
          onClick={onClose}
        />
      )}

      <div
        className={`fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl flex flex-col transform transition-transform duration-300 z-[1000] ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* HEADER */}
        <div className="flex justify-between items-center px-5 py-4 border-b bg-orange-50">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FaShoppingCart className="text-orange-500" />
            Your Cart
          </h2>

          <div className="flex items-center gap-3">
            {cartItems.length > 0 && (
              <button
                //onClick={clearCart}
                onClick={() => setShowClearConfirm(true)}
                className="text-sm text-red-600 flex items-center gap-1"
              >
                <FaTrash /> Clear
              </button>
            )}
            <button onClick={onClose}>
              <IoMdClose size={22} />
            </button>
          </div>
        </div>
        {/* ITEMS */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {displayBrand && (
              <h5 className="text-xl font-bold text-[#000048] mb-4">
                {displayBrand}
              </h5>
            )}
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <FaShoppingCart size={48} className="text-gray-300" />
              <p className="mt-3">Your cart is empty</p>
              
            </div>
          ) : (
            <>
              {vendorId && (
                <div className="p-3 bg-blue-50 border text-sm rounded-lg">
                  🏪 Vendor ID: <strong>{vendorId}</strong>
                </div>
              )}

              {validatedCart
                .filter((item) => !item.isExpired)
                .map((item) => {
                const price = item.unit_price || item.price || 0;
                const normalizedItemType = (item.item_type && item.item_type !== "other") 
                  ? item.item_type 
                  : getItemType(item.service_type);

                return (
                  <div
                    key={item.cartItemId || item._id}
                    // CHANGE 1: Apply red border if expired
                    className={`border rounded-xl p-4 transition-colors ${
                      item.isExpired ? "border-red-400 bg-red-50" : "bg-gray-50"
                    }`}
                  >
                    <div className="flex justify-between">
                      <p className="font-semibold flex items-center gap-2">
                        {item.name || item.service_type || "Service"}
                        {/* CHANGE 2: Show a badge if expired */}
                        {item.isExpired && (
                          <span className="text-[10px] bg-red-600 text-white px-2 py-0.5 rounded-full uppercase">
                            Invalid Date
                          </span>
                        )}
                      </p>

                      <button
                       // onClick={() => removeFromCart(item.cartItemId || item._id)}
                       onClick={() => setDeleteItemId(item.cartItemId || item._id)}

                        className="text-red-500"
                      >
                        <FaTrash size={14} />
                      </button>
                    </div>
                    {/*  <p className="text-sm mt-1">
                      Vendor: <strong>{brandName}</strong>
                    </p>*/}

                    <p className="text-sm mt-1 capitalize">
                      🏷️ Type: {normalizedItemType.replace("_", " ")}
                    </p>

                    <p className="text-sm mt-1 flex items-center gap-1">
                      <FaCalendarAlt className="text-orange-500" />
                      {getDateDisplay(item)}
                    </p>

                    {item.item_type === "boarding" && item.room_name && (
                      <p className="text-sm mt-1 flex items-center gap-1">
                        <FaHome className="text-orange-500" />
                        Room: {item.room_name}
                      </p>
                    )}

                    {item.duration && (
                      <p className="text-sm flex items-center mt-1">
                        <FaHourglassHalf className="text-orange-500 mr-2" />
                       Duration : {(() => {
                          if (typeof item.duration === 'string' && 
                              /hr|min|day/i.test(item.duration )) {
                            return item.duration;
                          }
                          // Convert to number
                          const durationNum = Number(item.duration) || 0;

                          // VET CLINIC FIX: Check if it's a vet service
                          if (item.service_type === "Pet Vet Clinic") {
                            return `${durationNum} min${durationNum > 1 ? "s" : ""}`;
                          }

                          // Original logic for other services
                          if (normalizedItemType === "grooming") {
                            return `${durationNum} min${durationNum > 1 ? "s" : ""}`;
                          } else if (normalizedItemType.includes("day")) {
                            return `${durationNum} hour${durationNum > 1 ? "s" : ""}`;
                          } else if (normalizedItemType === "boarding") {
                            return `${durationNum} day${durationNum > 1 ? "s" : ""}`;
                          }
                          // Default to minutes if duration is small
                          else if (durationNum < 120) {
                            return `${durationNum} min${durationNum > 1 ? "s" : ""}`;
                          } else {
                            return `${durationNum} day${durationNum > 1 ? "s" : ""}`;
                          }
                        })()}
                      </p>
                    )}
                    
                    {(
                      item.item_type?.toLowerCase().includes("day_care") ||
                      item.item_type?.toLowerCase().includes("dog_walking")
                    ) && item.frequency && (
                      <p className="text-sm flex items-center mt-1">
                        <TbRepeat className="text-blue-500 mr-2 text-base font-bold " />
                         Frequency : {item.frequency
                          .split("_")
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                          .join(" ")}
                      </p>
                    )}

                    {item.addons?.length > 0 && (
                      <p className="text-sm text-gray-500 mt-1">
                        Addons: {item.addons.map((a) => a.name).join(", ")}
                      </p>
                    )}

                    <hr className="my-2" />

                    {/* PRICE BREAKDOWN (Boarding Only) */}

                    {normalizedItemType === "boarding" && item.priceMeta ? (
                      /* --- BOARDING CASE (Math Breakdown) --- */
                      <div className="text-sm space-y-1 bg-white p-2 rounded border border-gray-100">
                        <div className="flex justify-between font-bold">
                          <span>Price Per Day</span>
                          <span>₹{item.priceMeta.base_price_per_day}</span>
                        </div>

                        {item.priceMeta.surchargeDetails &&
                        item.priceMeta.surchargeDetails.length > 0 ? (
                          <>
                            {/* List of individual surcharges */}
                            {item.priceMeta.surchargeDetails.map((s, i) => (
                              <div
                                key={i}
                                className="flex justify-between text-gray-700 text-sm"
                              >
                                <span>{getSurchargeLabel(s.name)}</span>
                                <span>₹{s.price}</span>
                              </div>
                            ))}

                            {/* Math Calculation Line: 399 + 150 = 549 */}
                            <div className="border-t border-dashed my-1 pt-1 flex justify-between text-sm font-bold text-gray-700">
                              <span>Price per day (incl. surcharges)</span>
                              <span>
                                ₹
                                {Math.round(
                                  item.priceMeta.final_price /
                                    item.priceMeta.days,
                                )}
                              </span>
                            </div>
                          </>
                        ) : null}

                        <div className="flex justify-between font-bold text-orange-600">
                          <span className="text-orange-600 text-sm">
                            Total Price ({item.priceMeta.days} Days)
                          </span>
                          <span className="text-orange-600 text-sm">
                            ₹{item.priceMeta.final_price}
                          </span>
                        </div>
                      </div>
                    ) : normalizedItemType === "grooming" && item.priceMeta ? (
                      /* --- GROOMING CASE (Original Logic) --- */
                      <div className="text-sm space-y-1">
                        <div className="flex justify-between font-bold">
                          <span>Basic price</span>
                          <span>
                            ₹
                            {(
                              item.priceMeta.base_price ||
                              item.priceMeta.base ||
                              item.unit_price ||
                              item.price ||
                              0
                            ).toFixed(0)}
                          </span>
                        </div>
                        {item.priceMeta.surchargeDetails?.map((s, i) => (
                          <div
                            key={i}
                            className="flex justify-between text-gray-700"
                          >
                            <span>{getSurchargeLabel(s.name)}</span>
                            <span>+ ₹{s.price}</span>
                          </div>
                        ))}
                        <div className="border-t pt-1 mt-1 flex justify-between font-semibold">
                          <span className="text-orange-600">Total</span>
                          <span className="text-orange-600">
                            ₹{item.priceMeta.final_price || item.price}
                          </span>
                        </div>
                      </div>
                    ) : (
                      /* Fallback (Grooming, Daycare, etc.) */
                      <div className="flex justify-between items-center font-bold text-sm">
                        <span className="text-orange-600 text-sm">
                          Total Price
                        </span>
                        <span className="text-orange-600">₹{price}</span>
                      </div>
                    )}
                  </div>
                );
                   })}
                  </>
                )}
              </div>

                {/* FOOTER */}
                {cartItems.length > 0 && (
              <div className="p-5 border-t bg-gray-50">
            
            {/* 1. Only show Total Amount if there are NO expired items */}
            {!hasExpiredItems && (
              <div className="flex justify-between text-lg font-semibold mb-4">
                <span>Total Amount</span>
                <span className="text-orange-600">₹{subtotal}</span>
              </div>
            )}

            {/* 2. Container for the two buttons */}
            <div className="flex gap-3">
              
              {/* Add Service Button - Always clickable */}
              <button
                className="flex-1 py-2 rounded-lg font-bold border-2 border-orange-500 text-orange-600 bg-white hover:bg-orange-50 transition-all"
                onClick={() => {
                  onClose();
                  navigate("/shop-detail");
                }}
              >
                Add Service
              </button>

              {/* Checkout Button - Changes text and disables if expired */}
              <button
                disabled={hasExpiredItems}
                className={`flex-1 py-2 rounded-lg font-bold transition-all ${
                  hasExpiredItems 
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed border border-gray-200" 
                  : "bg-orange-500 text-white hover:bg-orange-600 shadow-md"
                }`}
                onClick={() => {
                  if (!hasExpiredItems) {
                    onClose();
                    navigate("/confirm");
                  }
                }}
              >
                {hasExpiredItems ? "Clear Invalid To Proceed" : "Proceed To Checkout"}
              </button>
            </div>
          </div>
        )}
     

      {/* DELETE ITEM CONFIRM */}   
      {deleteItemId && (
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm"
            onClick={() => setDeleteItemId(null)}
          >
            <div
              className="bg-white w-[90%] max-w-sm rounded-2xl shadow-2xl p-6 text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-red-500 text-4xl mb-3">🗑️</div>

              <h3 className="text-lg font-semibold mb-2">
                Remove Item?
              </h3>

              <p className="text-sm text-gray-600 mb-5">
                Are you sure you want to remove{" "}
                  <span className="font-semibold text-black">
                    "{itemToDelete?.name || itemToDelete?.service_type}"
                  </span>{" "}
                from your cart?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteItemId(null)}
                  className="flex-1 py-2 rounded-lg border text-gray-600 hover:bg-gray-300"
                >
                  Cancel
                </button>

                <button
                  onClick={() => {
                    removeFromCart(deleteItemId);
                    setDeleteItemId(null);
                  }}
                  className="flex-1 py-2 rounded-lg bg-red-500 text-white hover:bg-orange-600"
                >
                  Yes, Remove
                </button>
              </div>
            </div>
          </div>
        )}
      {/* CLEAR CART CONFIRM */}
        {showClearConfirm && (
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm"
            onClick={() => setShowClearConfirm(false)}
          >
            <div
          className="bg-white w-[90%] max-w-sm rounded-2xl shadow-2xl p-6 text-center"
          onClick={(e) => e.stopPropagation()}
        >
              <div className="text-orange-500 text-4xl mb-3">⚠️</div>

              <h3 className="text-lg font-semibold mb-2">
                Clear Entire Cart?
              </h3>

              <p className="text-sm text-gray-600 mb-5">
                This will remove all services from your cart.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowClearConfirm(false)}
                  className="flex-1 py-2 rounded-lg border text-gray-600 hover:bg-gray-300"
                >
                  Keep Cart
                </button>
                <button
                  onClick={() => {
                    clearCart();
                    setShowClearConfirm(false);
                  }}
                  className="flex-1 py-2 rounded-lg bg-red-500 text-white hover:bg-orange-600"
                >
                  Clear Cart
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </>,
    document.body
  );
};

export default CartDrawer;
